// Gregorian -> Jalali (Solar Hijri) date conversion and Persian formatting.
// Kept self-contained on purpose: the app runs on both Hermes and the web, and
// neither platform exposes a reliable Jalali formatter through `Intl`.

const JALALI_MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
];

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

// The news API always serves publication times in Iran Standard Time (+03:30).
const IRAN_OFFSET_MS = (3 * 60 + 30) * 60 * 1000;

// Cumulative days before the first of each Gregorian month (non-leap year).
const GREGORIAN_MONTH_DAYS = [
  0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334,
];

type JalaliDate = {
  jy: number;
  jm: number;
  jd: number;
};

const toJalali = (gy: number, gm: number, gd: number): JalaliDate => {
  let year = gy;
  let jy = year <= 1600 ? 0 : 979;
  year -= year <= 1600 ? 621 : 1600;

  const leapBase = gm > 2 ? year + 1 : year;
  let days =
    365 * year +
    Math.floor((leapBase + 3) / 4) -
    Math.floor((leapBase + 99) / 100) +
    Math.floor((leapBase + 399) / 400) -
    80 +
    gd +
    GREGORIAN_MONTH_DAYS[gm - 1];

  jy += 33 * Math.floor(days / 12053);
  days %= 12053;

  jy += 4 * Math.floor(days / 1461);
  days %= 1461;

  if (days > 365) {
    jy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }

  const jm =
    days < 186 ? 1 + Math.floor(days / 31) : 7 + Math.floor((days - 186) / 30);
  const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);

  return { jy, jm, jd };
};

const padTwo = (value: number): string => String(value).padStart(2, '0');

/** Rewrites the ASCII digits of `value` with their Persian counterparts. */
export const toPersianDigits = (value: string | number): string =>
  String(value).replace(/[0-9]/g, digit => PERSIAN_DIGITS[Number(digit)]);

/**
 * Formats an RFC 3339 publication time (served by the API in Iran Standard
 * Time) as a Jalali date and clock time, e.g. `۱۲ شهریور ۱۴۰۵، ساعت ۰۸:۴۰`.
 * Returns an empty string when the source carries no parseable timestamp.
 */
export const formatJalaliDateTime = (time: string): string => {
  const timestamp = Date.parse(time);

  if (!time || Number.isNaN(timestamp)) {
    return '';
  }

  const iranTime = new Date(timestamp + IRAN_OFFSET_MS);
  const { jy, jm, jd } = toJalali(
    iranTime.getUTCFullYear(),
    iranTime.getUTCMonth() + 1,
    iranTime.getUTCDate(),
  );

  const clock = `${padTwo(iranTime.getUTCHours())}:${padTwo(
    iranTime.getUTCMinutes(),
  )}`;

  return toPersianDigits(`${jd} ${JALALI_MONTHS[jm - 1]} ${jy}، ساعت ${clock}`);
};
